import React from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

export default function ICMemoView({ opportunity, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card z-10">
          <div>
            <p className="font-caption text-xs text-muted-foreground uppercase tracking-wider">Investment Committee</p>
            <h2 className="font-heading text-xl font-semibold text-foreground">One-Page Summary</h2>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" iconName="Download" iconPosition="left" iconSize={14} onClick={() => {}}>
              Export PDF
            </Button>
            <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-muted transition-colors">
              <Icon name="X" size={18} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Title Block */}
          <div className="p-4 rounded-lg bg-primary text-white">
            <p className="font-caption text-xs opacity-70 uppercase tracking-wider mb-1">Confidential — IC Use Only</p>
            <h3 className="font-heading text-xl font-semibold">{opportunity?.name}</h3>
            <div className="flex flex-wrap gap-3 mt-2 text-sm opacity-90">
              <span>{opportunity?.sector} · {opportunity?.region}</span>
              <span>·</span>
              <span>Target IRR: {opportunity?.targetIRR}</span>
              <span>·</span>
              <span>{opportunity?.stage}</span>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "Project Value", value: opportunity?.projectValue },
              { label: "Equity Ticket", value: opportunity?.ticketSize },
              { label: "Target IRR", value: opportunity?.targetIRR },
              { label: "Hold Period", value: opportunity?.holdPeriod },
            ]?.map((m) => (
              <div key={m?.label} className="p-3 rounded-md bg-muted/50 text-center">
                <p className="font-data text-lg font-semibold text-foreground">{m?.value}</p>
                <p className="font-caption text-xs text-muted-foreground">{m?.label}</p>
              </div>
            ))}
          </div>

          {/* Two Column */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <h4 className="font-heading text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                <Icon name="CheckCircle" size={14} color="var(--color-success)" /> Investment Rationale
              </h4>
              <ul className="space-y-1.5">
                {opportunity?.highlights?.slice(0, 4)?.map((h, i) => (
                  <li key={i} className="font-caption text-sm text-muted-foreground flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span> {h}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-heading text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                <Icon name="AlertTriangle" size={14} color="var(--color-warning)" /> Key Risks
              </h4>
              <ul className="space-y-1.5">
                {["Construction cost overrun (mitigated via EPC)", "Revenue shortfall (CfD floor protection)", "Regulatory change (planning secured)", "Interest rate risk (fully hedged)"]?.map((r, i) => (
                  <li key={i} className="font-caption text-sm text-muted-foreground flex items-start gap-2">
                    <span className="text-warning mt-0.5">•</span> {r}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* ESG */}
          <div className="p-4 rounded-lg bg-success/5 border border-success/20">
            <h4 className="font-heading text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
              <Icon name="Leaf" size={14} color="var(--color-success)" /> ESG Summary
            </h4>
            <div className="grid grid-cols-3 gap-3 text-center">
              {[
                { label: "ESG Rating", value: "A+" },
                { label: "CO₂ Avoided", value: "85kt/yr" },
                { label: "SFDR", value: "Art. 9" },
              ]?.map((e) => (
                <div key={e?.label}>
                  <p className="font-data font-semibold text-success">{e?.value}</p>
                  <p className="font-caption text-xs text-muted-foreground">{e?.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendation */}
          <div className="p-4 rounded-lg border-2 border-primary/30 bg-primary/5">
            <h4 className="font-heading text-sm font-semibold text-primary mb-1">IC Recommendation</h4>
            <p className="font-caption text-sm text-foreground">
              Proceed to full due diligence and SPV structuring. Opportunity meets investment criteria on IRR, ESG, and risk-adjusted return basis. Recommend anchor commitment of £15m subject to satisfactory completion of legal and technical DD.
            </p>
          </div>

          <p className="font-caption text-xs text-muted-foreground text-center">
            Prepared by SeedDrive Platform · {new Date()?.toLocaleDateString("en-GB")} · Confidential
          </p>
        </div>
      </div>
    </div>
  );
}